import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Navbar from '../../components/storefront/layout/Navbar';
import Footer from '../../components/storefront/layout/Footer';
import HeroCarousel from '../../components/storefront/home/HeroCarousel';
import ProductSlider from '../../components/storefront/home/ProductSlider';
import ProductSliderSkeleton from '../../components/storefront/home/ProductSliderSkeleton';
import CategorySlider from '../../components/storefront/home/CategorySlider';
import EmptyProductState from '../../components/storefront/home/EmptyProductState';
import { mapProductToCard } from '../../components/storefront/home/mapProductToCard';
import { useGetStorefrontCategoriesQuery } from '../../store/slices/storefrontCategoriesSlice';
import {
  useGetFeaturedProductsQuery,
  useGetOnSaleProductsQuery,
  useGetBestSellerProductsQuery,
} from '../../store/slices/storefrontProductsSlice';
import { selectCartItemCount } from '../../store/slices/cartSlice';
import { getImageUrl, API_CONFIG } from '../../config/api';
import { useSEO } from '../../hooks/useSEO';

// Explicit list of product sliders shown on the homepage, in display order.
// Each entry targets a specific category or subcategory by id. "Dried Fruits"
// is a subcategory; the rest are top-level categories.
const HOMEPAGE_SLIDERS: { title: string; id: string; type: 'category' | 'subcategory' }[] = [
  { title: 'Nuts', id: '22db8d16-9693-4e48-980b-825cf71dcb1d', type: 'category' },
  { title: 'Seeds', id: 'cc080420-5f64-49b7-975a-897cd7fb7c31', type: 'category' },
  { title: 'Dried Fruits', id: '4a0a2277-ebac-4946-9aa3-bf9912ba02ed', type: 'subcategory' },
  { title: 'Chocolates', id: '17ce05b2-a09b-450b-87a5-161e49c7db5e', type: 'category' },
  { title: 'Sweets', id: '8d486dcb-d88e-4ce1-a948-556adc3ff676', type: 'category' },
  { title: 'Turkish Delights', id: 'f98a2797-f0ed-49b7-b5c9-b03d2fabdeaa', type: 'category' },
];

export default function HomePage() {
  useSEO({
    description: 'Shop premium quality nuts, dried fruits, and healthy snacks at The Nut Shop. Fresh products delivered to your door.',
  });

  // Fetch categories for navbar, footer, and the homepage category sliders
  const { data: categories = [], isLoading: categoriesLoading } =
    useGetStorefrontCategoriesQuery();

  // Curated product sliders
  const { data: featuredProducts = [], isLoading: featuredLoading } =
    useGetFeaturedProductsQuery();
  const { data: onSaleProducts = [], isLoading: onSaleLoading } =
    useGetOnSaleProductsQuery();
  const { data: bestSellerProducts = [], isLoading: bestSellerLoading } =
    useGetBestSellerProductsQuery();

  // Get cart item count for navbar
  const cartItemCount = useSelector(selectCartItemCount);

  // Build the homepage sliders from the explicit config. For a subcategory we
  // resolve its parent category id (from the categories data) so the "View All"
  // link can use the /category/:categoryId/:subcategoryId route.
  const sliders = useMemo(() => {
    return HOMEPAGE_SLIDERS.map((entry) => {
      let viewAllLink: string | undefined;
      if (entry.type === 'category') {
        viewAllLink = `/category/${entry.id}`;
      } else {
        const parent = categories.find((cat) =>
          cat.subcategories?.some((sub) => sub.id === entry.id)
        );
        viewAllLink = parent ? `/category/${parent.id}/${entry.id}` : undefined;
      }
      return { ...entry, viewAllLink };
    });
  }, [categories]);

  // Hero carousel - fetch from API
  const [heroSlides, setHeroSlides] = useState<{
    id: string; imageUrl: string; heading: string; subheading: string;
    ctaText: string; ctaLink: string; buttonColor?: string;
  }[]>([]);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(`${API_CONFIG.API_URL}/banner`);
        if (res.ok) {
          const data = await res.json();
          if (data.result?.length > 0) {
            setHeroSlides(data.result.map((b: any) => ({
              id: b.id,
              imageUrl: getImageUrl(b.imageUrl),
              heading: b.heading,
              subheading: b.subheading,
              ctaText: b.buttonText || 'Shop Now',
              ctaLink: b.buttonLink || '/products/best-sellers',
              buttonColor: b.buttonColor || undefined,
            })));
            setHeroLoading(false);
            return;
          }
        }
      } catch { /* fall through to defaults */ }
      // Fallback slides if no banners configured
      setHeroSlides([
        {
          id: '1',
          imageUrl: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=1920&h=500&fit=crop',
          heading: 'Premium Quality Nuts',
          subheading: 'Discover our selection of fresh, organic nuts delivered to your door',
          ctaText: 'Shop Now',
          ctaLink: '/products/best-sellers',
        },
        {
          id: '2',
          imageUrl: 'https://images.unsplash.com/photo-1608797178974-15b35a64ede9?w=1920&h=500&fit=crop',
          heading: 'Special Offers',
          subheading: 'Save big on your favorite nuts and dried fruits',
          ctaText: 'Shop Now',
          ctaLink: '/products/on-sale',
        },
        {
          id: '3',
          imageUrl: 'https://images.unsplash.com/photo-1613919113640-25732ec5e61f?w=1920&h=500&fit=crop',
          heading: 'Healthy Snacking',
          subheading: 'Nutritious and delicious options for every lifestyle',
          ctaText: 'Shop Now',
          ctaLink: '/products/featured',
        },
      ]);
      setHeroLoading(false);
    };
    fetchBanners();
  }, []);

  if (categoriesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Navbar */}
      <Navbar
        categories={categories}
        cartItemCount={cartItemCount}
      />

      {/* Main Content - Add top padding to account for fixed navbar */}
      <main id="main-content" className="pt-navbar-offset">
        {/* Hero Carousel */}
        {!heroLoading && <HeroCarousel slides={heroSlides} />}

        {/* Featured Products */}
        {featuredLoading ? (
          <ProductSliderSkeleton />
        ) : (
          <ProductSlider
            title="Featured Products"
            viewAllLink="/products/featured"
            products={featuredProducts.map(mapProductToCard)}
          />
        )}

        {/* On Sale */}
        {onSaleLoading ? (
          <ProductSliderSkeleton />
        ) : (
          <ProductSlider
            title="On Sale"
            viewAllLink="/products/on-sale"
            products={onSaleProducts.map(mapProductToCard)}
          />
        )}

        {/* Best Sellers */}
        {bestSellerLoading ? (
          <ProductSliderSkeleton />
        ) : (
          <ProductSlider
            title="Best Sellers"
            viewAllLink="/products/best-sellers"
            products={bestSellerProducts.map(mapProductToCard)}
          />
        )}

        {/* One product slider per configured category / subcategory */}
        {sliders.map((slider) => (
          <CategorySlider
            key={slider.id}
            id={slider.id}
            type={slider.type}
            title={slider.title}
            viewAllLink={slider.viewAllLink}
          />
        ))}

        {/* Empty State - only when there is genuinely nothing to show */}
        {!featuredLoading &&
          !onSaleLoading &&
          !bestSellerLoading &&
          featuredProducts.length === 0 &&
          onSaleProducts.length === 0 &&
          bestSellerProducts.length === 0 && (
            <EmptyProductState
              title="No products available yet"
              message="Check back soon for our amazing selection!"
            />
          )}
      </main>

      {/* Footer */}
      <Footer categories={categories} />
    </div>
  );
}
